import { rememberKeyImage } from '../../keyimages';
import { risClient } from '../../session';
import type { AssistantFinding } from '../api';
import { conversationStudy, frameAt, resolveLocation } from '../context';
import { type ImageTarget, keyImageBody, reviewedLabel } from './logic';
import { toolFailure, type RadiologistDeps } from './ui';

/**
 * "Imagen clave" on a reviewed finding with a location (contract v3 §2.8): the finding's image is
 * stored as a key image through the existing `PUT key-images` (viewer:annotate), described by the
 * finding's label (its correction when corrected). The key-image star and list update at once.
 */

export const KEY_FINDING_TEXT = {
  button: 'Imagen clave',
  done: (label: string) => `Imagen clave guardada: ${label}`,
  notInViewer: 'La imagen del hallazgo no está cargada en el visor.',
  failed: 'No fue posible guardar la imagen clave.',
} as const;

/** The finding's image: from the series in the viewer when loaded, else from the location ris sent. */
function imageTarget(deps: RadiologistDeps, finding: AssistantFinding): { target: ImageTarget; study: string } | null {
  const location = finding.location;
  if (!location) return null;
  const resolved = resolveLocation(deps.shell, location);
  if (resolved) {
    const at = frameAt(resolved.series, resolved.index);
    if (!at) return null;
    return {
      study: resolved.series.studyInstanceUID,
      target: { seriesUid: resolved.series.seriesInstanceUID, sopUid: at.instance.sopInstanceUID, frame: at.frame, index: resolved.index, seriesDescription: resolved.series.description || null },
    };
  }
  if (!location.instance_uid) return null;
  return {
    study: conversationStudy(deps.shell),
    target: { seriesUid: location.series_uid, sopUid: location.instance_uid, frame: location.frame, index: location.index ?? 0, seriesDescription: null },
  };
}

export async function markFindingKeyImage(deps: RadiologistDeps, finding: AssistantFinding): Promise<void> {
  const client = risClient();
  const where = imageTarget(deps, finding);
  if (!client || !where) return void deps.shell.toast(KEY_FINDING_TEXT.notInViewer, 'warning');
  try {
    const stored = await client.markKeyImage(where.study, keyImageBody(finding, where.target));
    rememberKeyImage(where.study, stored);
    deps.shell.toast(KEY_FINDING_TEXT.done(reviewedLabel(finding)));
  } catch (error) {
    console.warn('[viewer] finding key image failed', error);
    deps.shell.toast(toolFailure(error, 'key_images', KEY_FINDING_TEXT.failed), 'error');
  }
}
